import React from 'react';

const DeployStatusUpdateView = ({
  fileId,
  loading,
  deploymentStatus,
  cardTemplates,
  selectedCardId,
  selectedStatus,
  appName,
  storeUrl,
  saving,
  error,
  success,
  sendEmail,
  userName,
  userEmail,
  getStatusLabel,
  handleSubmit,
  handleStoreUrlChange,
  handleCardSelect,
  handleStatusSelect,
  handleSendEmailChange,
  handleBackClick,
  reloadData
}) => {
  return (
    <div className="bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="bg-gray-800 py-4 px-6 shadow-md mb-6">
        <div className="container mx-auto flex items-center justify-between">
          <button 
            onClick={handleBackClick}
            className="flex items-center text-blue-400 hover:text-blue-300 transition-colors"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </button>
          <h1 className="text-2xl font-bold text-white">Update Deployment Status</h1>
          <button 
            onClick={reloadData}
            disabled={loading}
            className={`flex items-center ${loading ? 'text-gray-500 cursor-not-allowed' : 'text-blue-400 hover:text-blue-300'} transition-colors`}
          >
            <svg className={`w-5 h-5 mr-1 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto">
            {/* App info card */}
            <div className="bg-gray-800 rounded-xl p-6 shadow-lg mb-6">
              <h2 className="text-xl font-bold text-white mb-2">
                Application: {appName || 'Unknown App'}
              </h2>
              <p className="text-sm text-gray-400 mb-4">
                File ID: {fileId}
              </p>
              
              {/* User information */}
              {(userName || userEmail) && (
                <div className="mb-4 text-gray-400">
                  <p className="text-sm">
                    <span className="text-gray-500">User: </span>
                    {userName && <span className="font-medium text-gray-300">{userName}</span>}
                    {userName && userEmail && <span> • </span>}
                    {userEmail && <span className="text-gray-400">{userEmail}</span>}
                  </p>
                </div>
              )}
              
              {error && (
                <div className="bg-red-900/30 border border-red-800 rounded-lg p-3 mb-4 text-red-400">
                  {error}
                </div>
              )}
              
              {success && (
                <div className="bg-green-900/30 border border-green-800 rounded-lg p-3 mb-4 text-green-400 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {saving ? 'Deployment status updated successfully!' : 'Data refreshed successfully!'}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* App URL Input */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Store URL
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={storeUrl}
                      onChange={handleStoreUrlChange}
                      placeholder="https://play.google.com/store/apps/details?id=com.example.app"
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    {success && (
                      <div className="absolute right-3 top-2 text-green-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Enter the app store URL if available
                  </p>
                </div>
                
                {/* Card selection */}
                <div className="mb-6">
                  <label htmlFor="cardSelect" className="block text-sm font-medium text-gray-400 mb-2">
                    Current Card
                  </label>
                  <select
                    id="cardSelect"
                    value={selectedCardId}
                    onChange={handleCardSelect}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {cardTemplates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.title}
                      </option>
                    ))}
                  </select>
                  <p className="text-sm text-gray-500 mt-1">
                    Select the current card in the deployment process
                  </p>
                </div>
                
                {/* Status selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Card Status
                  </label>
                  
                  <div className="grid grid-cols-3 gap-4">
                    {['pending', 'in-progress', 'completed'].map((status) => {
                      const isSelected = selectedStatus === status;
                      
                      let buttonClass = "";
                      if (isSelected) {
                        if (status === 'completed') {
                          buttonClass = 'border-green-600 bg-green-900/30 text-green-400';
                        } else if (status === 'in-progress') {
                          buttonClass = 'border-blue-600 bg-blue-900/30 text-blue-400';
                        } else { // pending
                          buttonClass = 'border-gray-600 bg-gray-700/50 text-gray-300';
                        }
                      } else {
                        buttonClass = 'border-gray-700 hover:border-gray-600 text-gray-400';
                      }
                      
                      return (
                        <div
                          key={status}
                          id={`status-button-${status}`}
                          className={`
                            flex items-center justify-center p-3 rounded-lg cursor-pointer border
                            ${buttonClass}
                          `}
                          onClick={() => handleStatusSelect(status)}
                        >
                          {getStatusLabel(status)}
                          {isSelected && <span className="ml-2">✓</span>}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Email notification option displayed directly under the status buttons when completed is selected */}
                  {selectedStatus === 'completed' && (
                    <div className="mt-3 flex items-center">
                      <input
                        type="checkbox"
                        id="sendEmailCheckbox"
                        checked={sendEmail}
                        onChange={handleSendEmailChange}
                        className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-600 focus:ring-blue-500"
                      />
                      <label htmlFor="sendEmailCheckbox" className="text-gray-300 text-sm">
                        Send status update email to user
                      </label>
                    </div>
                  )}
                </div>
                
                {/* Submit button */}
                <button
                  type="submit"
                  disabled={saving}
                  className={`
                    w-full py-3 rounded-lg text-white font-medium
                    ${
                      saving
                        ? 'bg-gray-700 cursor-not-allowed'
                        : 'bg-primary hover:bg-primary-light'
                    }
                  `}
                >
                  {saving ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Saving Changes...
                    </span>
                  ) : (
                    'Update Deployment Status'
                  )}
                </button>
              </form>
            </div>
            
            {/* Current deployment status summary */}
            {deploymentStatus && deploymentStatus.id && (
              <div className="bg-gray-800 rounded-xl p-6 shadow-lg">
                <h3 className="text-lg font-semibold text-white mb-4">Current Status</h3>
                
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-500">Overall Status</p>
                    <p className={`
                      ${
                        deploymentStatus.status === 'completed' ? 'text-green-400' :
                        deploymentStatus.status === 'in-progress' ? 'text-blue-400' :
                        'text-gray-300'
                      }
                    `}>
                      {getStatusLabel(deploymentStatus.status)}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Current Stage</p>
                    <p className="text-white">
                      {
                        deploymentStatus.currentStage && cardTemplates.find(t => t.id === deploymentStatus.currentStage)
                          ? cardTemplates.find(t => t.id === deploymentStatus.currentStage).title
                          : 'Not set'
                      }
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Started At</p>
                    <p className="text-gray-300">
                      {deploymentStatus.startedAt 
                        ? new Date(deploymentStatus.startedAt).toLocaleString()
                        : 'Not started'
                      }
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Last Updated</p>
                    <p className="text-gray-300">
                      {deploymentStatus.updatedAt
                        ? new Date(deploymentStatus.updatedAt).toLocaleString()
                        : 'Never'
                      }
                    </p>
                  </div>
                </div>
                
                {/* Stages summary */}
                {deploymentStatus.stages && Object.keys(deploymentStatus.stages).length > 0 && (
                  <div className="mt-6 pt-6 border-t border-gray-700">
                    <h4 className="font-medium text-white mb-3">Stages Summary</h4>
                    <div className="space-y-2">
                      {Object.entries(deploymentStatus.stages).map(([stageId, stage]) => {
                        const template = cardTemplates.find(t => t.id === stageId);
                        // Only show if template exists and is a stage type
                        if (template && template.type === 'stage') {
                          return (
                            <div key={stageId} className="flex items-center">
                              <div className={`w-3 h-3 rounded-full mr-2 flex-shrink-0 ${
                                stage.status === 'completed' ? 'bg-green-500' :
                                stage.status === 'in-progress' ? 'bg-blue-500' :
                                'bg-gray-600'
                              }`} />
                              <span className="text-gray-300 text-sm mr-2">
                                {template.title}:
                              </span>
                              <span className={`text-sm ${
                                stage.status === 'completed' ? 'text-green-400' :
                                stage.status === 'in-progress' ? 'text-blue-400' :
                                'text-gray-500'
                              }`}>
                                {getStatusLabel(stage.status)}
                              </span>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeployStatusUpdateView; 